/**
 * Product Variant Gallery Manager - Versión Simplificada
 * Lógica: Variante principal muestra todas las imágenes, otras solo la primera
 */

if (!customElements.get('variant-gallery-manager')) {
  customElements.define(
    'variant-gallery-manager',
    class VariantGalleryManager extends HTMLElement {
      constructor() {
        super();
        this.mediaGallery = null;
        this.currentVariantId = null;
        this.mainVariantId = null; // Variante principal (inicial)
        this.allMediaItems = [];
        this.allThumbnailItems = [];
        this.isInitialized = false;
      }

      connectedCallback() {
        this.init();
        this.setupEventListeners();
      }

      init() {
        // Buscar la galería de medios
        this.mediaGallery = document.querySelector('media-gallery');
        if (!this.mediaGallery) {
          console.warn('Media gallery no encontrada');
          return;
        }

        // Detectar variante principal (la que está seleccionada al cargar)
        this.detectMainVariant();

        // Guardar referencia a todos los elementos de media
        this.cacheMediaElements();
        
        this.isInitialized = true;
        console.log('Variant Gallery Manager inicializado', {
          mainVariantId: this.mainVariantId,
          currentVariantId: this.currentVariantId
        });
      }

      detectMainVariant() {
        // Intentar obtener de URL primero
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = urlParams.get('variant');
        
        if (urlVariantId) {
          this.mainVariantId = urlVariantId;
          this.currentVariantId = urlVariantId;
        } else {
          // Obtener de data-attribute de la galería
          this.mainVariantId = this.mediaGallery.dataset.currentVariantId;
          this.currentVariantId = this.mainVariantId;
        }

        // Fallback: obtener desde variant-selects
        if (!this.mainVariantId) {
          const variantSelects = document.querySelector('variant-selects');
          if (variantSelects) {
            const selectedVariant = variantSelects.querySelector('[data-selected-variant]');
            if (selectedVariant) {
              try {
                const variant = JSON.parse(selectedVariant.innerHTML);
                this.mainVariantId = variant.id.toString();
                this.currentVariantId = this.mainVariantId;
              } catch (e) {
                console.warn('No se pudo obtener variante desde variant-selects');
              }
            }
          }
        }
      }

      cacheMediaElements() {
        // Cachear elementos de la galería principal
        const mediaList = this.mediaGallery.querySelector('.product__media-list');
        if (mediaList) {
          this.allMediaItems = Array.from(mediaList.querySelectorAll('li[data-media-id]'));
        }

        // Cachear elementos de thumbnails
        const thumbnailList = this.mediaGallery.querySelector('.thumbnail-list');
        if (thumbnailList) {
          this.allThumbnailItems = Array.from(thumbnailList.querySelectorAll('li[data-target]'));
        }
      }

      setupEventListeners() {
        // Escuchar cambios de variante
        document.addEventListener('variant:changed', this.handleVariantChange.bind(this));
        
        // Escuchar eventos de PUB_SUB si están disponibles
        if (window.subscribe && window.PUB_SUB_EVENTS) {
          window.subscribe(window.PUB_SUB_EVENTS.variantChange, this.handleVariantChangeEvent.bind(this));
        }
      }

      handleVariantChange(event) {
        const variantId = event.detail?.variant?.id || event.detail?.variantId;
        if (variantId && variantId.toString() !== this.currentVariantId) {
          this.applyGalleryLogic(variantId.toString());
          this.updateURL(variantId);
        }
      }

      handleVariantChangeEvent(event) {
        const variant = event.data?.variant;
        if (variant && variant.id.toString() !== this.currentVariantId) {
          this.applyGalleryLogic(variant.id.toString());
          this.updateURL(variant.id);
        }
      }

      applyGalleryLogic(variantId) {
        if (!this.isInitialized) return;

        console.log('Aplicando lógica de galería:', {
          variantId,
          mainVariantId: this.mainVariantId,
          isMainVariant: variantId === this.mainVariantId
        });
        
        this.currentVariantId = variantId;

        if (variantId === this.mainVariantId) {
          // Es la variante principal: mostrar TODAS las imágenes
          this.showAllImages();
        } else {
          // Es otra variante: mostrar solo la PRIMERA imagen
          this.showOnlyFirstImage();
        }
        
        // Anunciar cambio para accesibilidad
        this.announceGalleryChange();
      }

      showAllImages() {
        console.log('Mostrando todas las imágenes (variante principal)');
        
        // Mostrar todos los elementos de la galería principal
        this.allMediaItems.forEach((item, index) => {
          item.classList.remove('is-hidden');
          item.style.display = '';
          
          // Activar el primer elemento
          if (index === 0) {
            item.classList.add('is-active');
          } else {
            item.classList.remove('is-active');
          }
        });

        // Mostrar todos los thumbnails
        this.allThumbnailItems.forEach((item) => {
          item.classList.remove('is-hidden');
          item.style.display = '';
        });

        this.updateSliderCounter();
      }

      showOnlyFirstImage() {
        console.log('Mostrando solo primera imagen (otra variante)');
        
        // En la galería principal: mostrar solo el primer elemento
        this.allMediaItems.forEach((item, index) => {
          if (index === 0) {
            item.classList.remove('is-hidden');
            item.style.display = '';
            item.classList.add('is-active');
          } else {
            item.classList.add('is-hidden');
            item.style.display = 'none';
            item.classList.remove('is-active');
          }
        });

        // En thumbnails: mostrar solo el primer elemento
        this.allThumbnailItems.forEach((item, index) => {
          if (index === 0) {
            item.classList.remove('is-hidden');
            item.style.display = '';
          } else {
            item.classList.add('is-hidden');
            item.style.display = 'none';
          }
        });

        this.updateSliderCounter();
      }

      updateSliderCounter() {
        const counter = this.mediaGallery.querySelector('.slider-counter--total');
        if (counter) {
          const visibleItems = this.allMediaItems.filter(item => !item.classList.contains('is-hidden'));
          counter.textContent = visibleItems.length;
        }
      }

      updateURL(variantId) {
        if (history.replaceState) {
          const url = new URL(window.location);
          url.searchParams.set('variant', variantId);
          history.replaceState({}, '', url);
        }
      }

      announceGalleryChange() {
        const liveRegion = this.mediaGallery.querySelector('[role="status"]');
        if (liveRegion) {
          const visibleCount = this.allMediaItems.filter(item => !item.classList.contains('is-hidden')).length;
          const isMainVariant = this.currentVariantId === this.mainVariantId;
          const message = isMainVariant 
            ? `Galería completa mostrada. ${visibleCount} imagen${visibleCount !== 1 ? 'es' : ''} disponible${visibleCount !== 1 ? 's' : ''}.`
            : `Vista simplificada. Mostrando imagen principal de la variante.`;
          
          liveRegion.textContent = message;
          
          // Limpiar el mensaje después de 2 segundos
          setTimeout(() => {
            liveRegion.textContent = '';
          }, 2000);
        }
      }

      // Método público para filtrar manualmente
      filterByVariant(variantId) {
        this.applyGalleryLogic(variantId.toString());
      }

      // Método público para obtener medios visibles
      getVisibleMedia() {
        return this.allMediaItems.filter(item => !item.classList.contains('is-hidden'));
      }

      // Método público para obtener la variante principal
      getMainVariantId() {
        return this.mainVariantId;
      }
    }
  );
}

// Auto-inicializar cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
  // Crear el elemento manager si no existe
  if (!document.querySelector('variant-gallery-manager')) {
    const manager = document.createElement('variant-gallery-manager');
    document.body.appendChild(manager);
  }
});

// Exportar para uso manual si es necesario
window.VariantGalleryManager = {
  init: () => {
    if (!document.querySelector('variant-gallery-manager')) {
      const manager = document.createElement('variant-gallery-manager');
      document.body.appendChild(manager);
    }
  },
  
  filterByVariant: (variantId) => {
    const manager = document.querySelector('variant-gallery-manager');
    if (manager && manager.filterByVariant) {
      manager.filterByVariant(variantId);
    }
  }
};